import React from "react";
import { HiPencil, HiPlusCircle } from "react-icons/hi2";
import { TanstackSortingFn } from "../../../utils/TableSorting";
import { CustomSwitch } from "../../ui/CustomSwitch/CustomSwitch";
import BaseTable from "../../ui/CustomTanStackTable/CustomTanStackTable";
import TableRowActionsMenu from "../../ui/TableRowActionsMenu/TableRowActionsMenu";
import ColumnVisibilitySelector from "../../ui/ColumnVisibilitySelector/ColumnVisibilitySelector";
import useColumnVisibilityStore, {
  selectVisibleColumns,
} from "../../../stores/columnVisibilityStore";
import { getDisplayValue } from "../../../utils/transactionutils";

const DEFAULT_COLUMN_KEYS = ["merchantName", "walletBalance"];

const OPTIONAL_COLUMN_KEYS = ["accountAlias", "accountStatus"];

const COLUMN_META = [
  { key: "merchantName", label: "Merchant Name" },
  { key: "walletBalance", label: "Account Balance" },
  { key: "accountAlias", label: "Account Alias" },
  { key: "accountStatus", label: "Status" },
];

function MerchantTableTanstack({
  data,
  onEdit,
  onToggleStatus,
  loadingIds,
  onMobileMoneyAdd,
  pagination,
  totalPages,
  totalElements,
  onPaginationChange,
  isFetching,
}) {
  const visibleOptionalKeys = useColumnVisibilityStore(
    selectVisibleColumns("merchants"),
  );
  const setVisibleOptionalKeys = useColumnVisibilityStore(
    (s) => s.setVisibleColumns,
  );

  const columnVisibility = React.useMemo(() => {
    const visibility = {};
    OPTIONAL_COLUMN_KEYS.forEach((key) => {
      visibility[key] = visibleOptionalKeys.includes(key);
    });
    return visibility;
  }, [visibleOptionalKeys]);

  const columns = React.useMemo(
    () => [
      {
        accessorKey: "merchantName",
        header: "Merchant Name",
        cell: (info) => (
          <div
            className="max-w-[160px] truncate"
            title={getDisplayValue(info.getValue())}
          >
            {getDisplayValue(info.getValue())}
          </div>
        ),
        sortingFn: TanstackSortingFn,
      },
      {
        accessorKey: "accountAlias",
        header: "Account Alias",
        cell: (info) => (
          <div
            className="max-w-[120px] truncate"
            title={getDisplayValue(info.getValue())}
          >
            {getDisplayValue(info.getValue())}
          </div>
        ),
        sortingFn: TanstackSortingFn,
      },
      {
        accessorKey: "walletBalance",
        header: "Account Balance",
        cell: (info) => (
          <div className="truncate" title={getDisplayValue(info.getValue())}>
            {getDisplayValue(info.getValue())}
          </div>
        ),
        sortingFn: TanstackSortingFn,
      },
      {
        header: "Status",
        accessorKey: "accountStatus",
        cell: ({ row }) => {
          const user = row.original;
          const isActive = user.accountStatus;
          const isLoading = loadingIds.has(user.merchantId);
          return (
            <CustomSwitch
              checked={isActive}
              disabled={isLoading}
              onChange={() => onToggleStatus(user)}
              activeLabel="Activated"
              inactiveLabel="Deactivated"
              color="primary"
            />
          );
        },
        enableSorting: false,
      },
      {
        id: "actions",
        header: "Actions",
        cell: (info) => {
          const row = info.row.original;
          return (
            <TableRowActionsMenu
              items={[
                {
                  label: "Edit",
                  icon: <HiPencil size={14} />,
                  onClick: () => onEdit?.(row),
                },
                {
                  label: "Add Mobile Money Account",
                  icon: <HiPlusCircle size={14} />,
                  onClick: () => onMobileMoneyAdd?.(row),
                },
              ]}
            />
          );
        },
        enableSorting: false,
      },
    ],
    [onEdit, onMobileMoneyAdd, onToggleStatus, loadingIds],
  );

  return (
    <div className="flex flex-col gap-3">
      <div className="flex justify-end mt-2">
        <ColumnVisibilitySelector
          columns={COLUMN_META}
          defaultColumnKeys={DEFAULT_COLUMN_KEYS}
          visibleColumnKeys={visibleOptionalKeys}
          onChange={(keys) => setVisibleOptionalKeys("merchants", keys)}
        />
      </div>
      <BaseTable
        data={data}
        columns={columns}
        columnVisibility={columnVisibility}
        pageSize={pagination?.pageSize}
        totalPages={totalPages}
        totalElements={totalElements}
        pagination={pagination}
        isFetching={isFetching}
        onPaginationChange={onPaginationChange}
        paginationMode="server"
        emptyMessage="No Merchants Found"
        rowClassName={() =>
          "hover:bg-blue-50 cursor-pointer bg-white-50 h-[50px] "
        }
      />
    </div>
  );
}

export default MerchantTableTanstack;
